import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Tags, Printer, Search, Ban } from 'lucide-react';
import { DataTable, type Column, type TableDensity } from '@/components/common/DataTable';
import { DensityToggle } from '@/components/common/DensityToggle';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { ErrorState } from '@/components/common/ErrorState';
import { KpiCard } from '@/components/common/KpiCard';
import { StatusTabs, type StatusTabOption } from '@/components/common/StatusTabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { dispatchService } from '@/services/dispatch';
import { fmt } from '@/lib/format';
import { printHtmlInWindow } from '@/lib/print';
import { useDebounced } from '@/hooks/useDebounced';
import type { DispatchLabelListRow, DispatchLabelStatus } from '@/types/dispatch';

type StatusFilter = 'all' | DispatchLabelStatus;

const STATUS_LABEL: Record<DispatchLabelStatus, string> = {
  DRAFT: 'Draft',
  GENERATED: 'Generated',
  PRINTED: 'Printed',
  ASSIGNED_TO_PARTNER: 'Assigned',
  CANCELLED: 'Cancelled',
};

const STATUS_TONE: Record<DispatchLabelStatus, 'muted' | 'success' | 'warning' | 'destructive'> = {
  DRAFT: 'muted',
  GENERATED: 'warning',
  PRINTED: 'success',
  ASSIGNED_TO_PARTNER: 'success',
  CANCELLED: 'destructive',
};

export function DispatchLabelsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [density, setDensity] = useState<TableDensity>('compact');
  useDebounced(search);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dispatch-labels'],
    queryFn: () => dispatchService.listLabels({ pageSize: 200 }),
  });

  const allRows = useMemo(() => {
    const rows = data?.rows ?? [];
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (r) =>
        r.labelNumber.toLowerCase().includes(q) ||
        r.dispatchOrder.dispatchNumber.toLowerCase().includes(q) ||
        r.dispatchOrder.salesOrder.orderNumber.toLowerCase().includes(q) ||
        (r.awbNumber ?? '').toLowerCase().includes(q),
    );
  }, [data, search]);

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = {
      all: allRows.length,
      DRAFT: 0,
      GENERATED: 0,
      PRINTED: 0,
      ASSIGNED_TO_PARTNER: 0,
      CANCELLED: 0,
    };
    for (const r of allRows) c[r.status] += 1;
    return c;
  }, [allRows]);

  const filtered = useMemo(() => {
    if (filter === 'all') return allRows;
    return allRows.filter((r) => r.status === filter);
  }, [allRows, filter]);

  const summary = useMemo(
    () => ({
      ready: counts.GENERATED + counts.PRINTED + counts.ASSIGNED_TO_PARTNER,
      drafts: counts.DRAFT,
      cancelled: counts.CANCELLED,
    }),
    [counts],
  );

  const tabs: StatusTabOption[] = [
    { value: 'all',                 label: 'All',       count: counts.all },
    { value: 'DRAFT',               label: 'Draft',     count: counts.DRAFT },
    { value: 'GENERATED',           label: 'Generated', count: counts.GENERATED },
    { value: 'PRINTED',             label: 'Printed',   count: counts.PRINTED },
    { value: 'ASSIGNED_TO_PARTNER', label: 'Assigned',  count: counts.ASSIGNED_TO_PARTNER },
    { value: 'CANCELLED',           label: 'Cancelled', count: counts.CANCELLED },
  ];

  // Reprint — re-fetches the BE-rendered HTML and opens a print window.
  // Same path as Slice 4's DispatchDetailSheet print button so behaviour
  // is consistent.
  const printMutation = useMutation({
    mutationFn: (labelId: string) => dispatchService.printLabel(labelId),
    onSuccess: (printable) => {
      const opened = printHtmlInWindow(printable.html, { width: 600, height: 800 });
      if (!opened) {
        toast.error('Popup blocked. Allow popups to print labels.');
        return;
      }
      toast.success('Label rendered');
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not render label'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: DispatchLabelStatus }) =>
      dispatchService.updateLabelStatus(id, { status }),
    onSuccess: (_d, vars) => {
      toast.success(`Label ${STATUS_LABEL[vars.status].toLowerCase()}`);
      qc.invalidateQueries({ queryKey: ['dispatch-labels'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not update label'),
  });

  const columns: Column<DispatchLabelListRow>[] = [
    {
      key: 'label',
      header: 'Label',
      width: '180px',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="font-mono text-[12px] font-medium text-foreground">{r.labelNumber}</div>
          <div className="font-mono text-[10.5px] text-muted-foreground">
            {r.dispatchOrder.dispatchNumber}
          </div>
        </div>
      ),
    },
    {
      key: 'order',
      header: 'Order',
      width: '160px',
      cell: (r) => (
        <span className="font-mono text-[11.5px] text-muted-foreground">
          {r.dispatchOrder.salesOrder.orderNumber}
        </span>
      ),
    },
    {
      key: 'partner',
      header: 'Partner',
      width: '110px',
      cell: (r) =>
        r.partnerCode ? (
          <span className="font-mono text-[11.5px]">{r.partnerCode}</span>
        ) : (
          <span className="text-[11.5px] text-muted-foreground">—</span>
        ),
    },
    {
      key: 'awb',
      header: 'AWB',
      cell: (r) =>
        r.awbNumber ? (
          <span className="font-mono text-[11.5px]">{r.awbNumber}</span>
        ) : (
          <span className="text-[11.5px] text-muted-foreground">—</span>
        ),
    },
    {
      key: 'package',
      header: 'Package',
      width: '140px',
      cell: (r) => {
        const dims =
          r.lengthCm && r.widthCm && r.heightCm
            ? `${r.lengthCm}×${r.widthCm}×${r.heightCm} cm`
            : '—';
        const weight = r.weightGrams != null ? `${r.weightGrams} g` : '—';
        return (
          <div className="space-y-0.5 text-[11.5px] text-muted-foreground">
            <div>{dims}</div>
            <div>{weight}</div>
          </div>
        );
      },
    },
    {
      key: 'created',
      header: 'Created at',
      width: '180px',
      cell: (r) => (
        <span className="font-mono text-[11.5px] tabular-nums text-muted-foreground">
          {fmt.dateTime(r.createdAt)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '130px',
      cell: (r) => (
        <Badge variant={STATUS_TONE[r.status]} className="text-[10px]">
          {STATUS_LABEL[r.status]}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '110px',
      hoverReveal: true,
      cell: (r) => (
        <div className="flex items-center justify-end gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={() => printMutation.mutate(r.id)}
                disabled={r.status === 'CANCELLED' || printMutation.isPending}
              >
                <Printer className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {r.status === 'PRINTED' ? 'Reprint label' : 'Print label'}
            </TooltipContent>
          </Tooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground">
                <Tags className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {r.status === 'DRAFT' && (
                <DropdownMenuItem
                  onClick={() => statusMutation.mutate({ id: r.id, status: 'GENERATED' })}
                >
                  Mark generated
                </DropdownMenuItem>
              )}
              {r.status === 'GENERATED' && (
                <DropdownMenuItem
                  onClick={() => statusMutation.mutate({ id: r.id, status: 'PRINTED' })}
                >
                  Mark printed
                </DropdownMenuItem>
              )}
              {r.status === 'PRINTED' && (
                <DropdownMenuItem
                  onClick={() =>
                    statusMutation.mutate({ id: r.id, status: 'ASSIGNED_TO_PARTNER' })
                  }
                >
                  Mark assigned to partner
                </DropdownMenuItem>
              )}
              {r.status !== 'CANCELLED' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => statusMutation.mutate({ id: r.id, status: 'CANCELLED' })}
                    className="text-destructive focus:text-destructive"
                  >
                    <Ban className="h-3.5 w-3.5" />
                    Cancel label
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">
        {!embedded && <ListPageHeader title="Dispatch labels" count={counts.all} />}

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <KpiCard label="Ready" value={fmt.number(summary.ready)} tone="primary" icon={Tags} />
          <KpiCard label="Drafts" value={fmt.number(summary.drafts)} tone="default" icon={Tags} />
          <KpiCard
            label="Cancelled"
            value={fmt.number(summary.cancelled)}
            tone={summary.cancelled ? 'destructive' : 'default'}
            icon={Ban}
          />
        </div>

        <StatusTabs value={filter} onChange={(v) => setFilter(v as StatusFilter)} options={tabs} />

        <div className="flex items-center justify-between gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search label # / dispatch # / order # / AWB…"
              className="h-8 pl-8 text-[13px]"
            />
          </div>
          <DensityToggle value={density} onChange={setDensity} />
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            density={density}
            emptyTitle={
              filter === 'all'
                ? 'No labels generated yet'
                : `No ${STATUS_LABEL[filter as DispatchLabelStatus].toLowerCase()} labels`
            }
            emptyDescription={
              filter === 'all'
                ? 'Labels are generated from a dispatch order — open a dispatch and hit Print label.'
                : 'Try a different status filter.'
            }
          />
        )}
      </div>
    </TooltipProvider>
  );
}
